import {
  select,
  scaleTime,
  scaleLinear,
  axisBottom,
  axisLeft,
  line,
  curveStepAfter,
  curveLinear,
  brushX,
  pointer,
  format,
} from "d3";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { findLeftIndex, findClosestPoint, tzTickFormat } from "../../utils/utility";
import { CHART_CONSTANTS } from "../../utils/constants";
import {
  CHART_COLORS,
  CHART_COLORS_DARK,
  FONT_FAMILY,
  MARGIN,
  SERIES_CONFIG,
  Y_AXIS_PADDING_MULTIPLIER,
  X_AXIS_LABEL_OFFSET,
  Y_AXIS_LABEL_OFFSET,
  TOOLTIP_BOTTOM_PADDING,
  BACKGROUND_BORDER_RADIUS,
} from "./constants";
import { buildTooltipHTML } from "./utils";

dayjs.extend(utc);
dayjs.extend(tz);

/**
 * Pure D3 rendering function for the LineBarComposedChart.
 * Draws axes, series lines, crosshair, tooltip interactions, brush zoom, and shift+drag pan.
 *
 * @param {object} params
 * @param {HTMLElement} params.container - The chart container DOM element
 * @param {SVGElement} params.svgEl - The SVG DOM element
 * @param {HTMLElement} params.tooltipEl - The tooltip DOM element
 * @param {object} params.allSeriesData - All series data keyed by SERIES_CONFIG.key
 * @param {object} params.visibility - Map of series name -> boolean visibility
 * @param {object} params.fullExtent - { minX, maxX, maxY }
 * @param {object|null} params.zoomExtremes - { min, max, source } or null
 * @param {React.MutableRefObject} params.isSyncingRef - Ref to prevent zoom feedback loops
 * @param {Function|null} params.onZoomChange - Callback for zoom changes
 * @param {string} params.clipPathId - Unique ID for the SVG clip path
 * @param {React.MutableRefObject} params.panCleanupRef - Ref to store pan listener cleanup
 * @param {boolean} params.isDark - Whether dark theme is active
 * @param {string} params.siteTimezone - IANA timezone string for the site
 */
export default function renderChart({
  container,
  svgEl,
  tooltipEl,
  allSeriesData,
  visibility,
  fullExtent,
  zoomExtremes,
  isSyncingRef,
  onZoomChange,
  clipPathId,
  panCleanupRef,
  isDark = false,
  siteTimezone,
}) {
  const colors = isDark ? CHART_COLORS_DARK : CHART_COLORS;
  if (!container || !svgEl) return;

  const width = container.clientWidth;
  const height = container.clientHeight;
  if (width <= 0 || height <= 0) return;

  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = height - MARGIN.top - MARGIN.bottom;
  if (innerWidth <= 0 || innerHeight <= 0) return;

  const svg = select(svgEl);
  svg.selectAll("*").remove();
  svg.attr("width", width).attr("height", height);

  // Background
  svg
    .append("rect")
    .attr("width", width)
    .attr("height", height)
    .attr("fill", colors.gridBackground)
    .attr("rx", BACKGROUND_BORDER_RADIUS);

  const g = svg.append("g").attr("transform", `translate(${MARGIN.left},${MARGIN.top})`);

  // Scales
  const xScale = scaleTime().range([0, innerWidth]);
  const yScale = scaleLinear().range([innerHeight, 0]);

  // Determine x domain from zoom or full extent
  xScale.domain([zoomExtremes ? zoomExtremes.min : fullExtent.minX, zoomExtremes ? zoomExtremes.max : fullExtent.maxX]);

  // Compute visible y max
  let visibleMaxY = 0;
  SERIES_CONFIG.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const d = allSeriesData[cfg.key];
    if (!d) return;
    d.forEach(point => {
      if (
        point[0] >= xScale.domain()[0] &&
        point[0] <= xScale.domain()[1] &&
        point[1] !== null &&
        point[1] !== undefined &&
        point[1] > visibleMaxY
      ) {
        visibleMaxY = point[1];
      }
    });
  });
  yScale.domain([0, visibleMaxY * Y_AXIS_PADDING_MULTIPLIER || 1]);

  // Clip path
  svg
    .append("defs")
    .append("clipPath")
    .attr("id", clipPathId)
    .append("rect")
    .attr("width", innerWidth)
    .attr("height", innerHeight);

  // Grid lines (Y)
  g.append("g")
    .attr("class", "grid-y")
    .call(axisLeft(yScale).tickSize(-innerWidth).tickFormat(""))
    .selectAll("line")
    .attr("stroke", colors.gridLines)
    .attr("stroke-dasharray", "4,4");
  g.select(".grid-y").select(".domain").remove();

  // X Axis — timezone-aware tick formatter
  const xAxisGen = axisBottom(xScale)
    .ticks(CHART_CONSTANTS.X_AXIS_TICK_COUNT)
    .tickFormat(date => tzTickFormat(date, siteTimezone));
  const xAxis = g.append("g").attr("transform", `translate(0,${innerHeight})`).call(xAxisGen);
  xAxis
    .selectAll("text")
    .attr("fill", colors.textSecondary)
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY);
  xAxis.selectAll("line").attr("stroke", colors.gridLines);
  xAxis.select(".domain").attr("stroke", colors.gridLines);

  // X Axis title
  g.append("text")
    .attr("x", innerWidth / 2)
    .attr("y", innerHeight + X_AXIS_LABEL_OFFSET)
    .attr("text-anchor", "middle")
    .attr("fill", colors.textPrimary)
    .style("font-weight", "500")
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY)
    .text("Time");

  // Y Axis — use comma formatting for thousands
  const yAxis = g.append("g").call(axisLeft(yScale).ticks(CHART_CONSTANTS.Y_AXIS_TICK_COUNT).tickFormat(format(",")));
  yAxis
    .selectAll("text")
    .attr("fill", colors.textSecondary)
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY);
  yAxis.selectAll("line").attr("stroke", colors.gridLines);
  yAxis.select(".domain").attr("stroke", colors.gridLines);

  // Y Axis title
  g.append("text")
    .attr("transform", "rotate(-90)")
    .attr("x", -innerHeight / 2)
    .attr("y", -Y_AXIS_LABEL_OFFSET)
    .attr("text-anchor", "middle")
    .attr("fill", colors.textPrimary)
    .style("font-weight", "500")
    .style("font-size", "12px")
    .style("font-family", FONT_FAMILY)
    .text("Power (W)");

  // Chart area with clip
  const chartArea = g.append("g").attr("clip-path", `url(#${clipPathId})`);

  // Sort series by zIndex (lower drawn first)
  const sortedSeries = [...SERIES_CONFIG].sort((a, b) => a.zIndex - b.zIndex);

  // Draw lines
  sortedSeries.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const seriesData = allSeriesData[cfg.key];
    if (!seriesData || !seriesData.length) return;

    const lineGen = line()
      .defined(d => d[0] !== null && d[0] !== undefined && d[1] !== null && d[1] !== undefined)
      .x(d => xScale(d[0]))
      .y(d => yScale(d[1]))
      .curve(cfg.stepLeft ? curveStepAfter : curveLinear);

    const path = chartArea
      .append("path")
      .datum(seriesData)
      .attr("fill", "none")
      .attr("stroke", cfg.color)
      .attr("stroke-width", cfg.lineWidth)
      .attr("d", lineGen)
      .attr("class", `series-line series-${cfg.key}`);

    if (cfg.dashed) {
      path.attr("stroke-dasharray", "6,3");
    }
  });

  // Crosshair
  const crosshair = g
    .append("line")
    .attr("class", "crosshair")
    .attr("y1", 0)
    .attr("y2", innerHeight)
    .attr("stroke", colors.textSecondary)
    .attr("stroke-width", 1)
    .attr("stroke-dasharray", "2,2")
    .attr("opacity", 0)
    .attr("pointer-events", "none");

  // Hover markers group
  const markersGroup = g.append("g").attr("class", "hover-markers").attr("pointer-events", "none");

  // Tooltip
  const tooltip = select(tooltipEl);

  // Brush for x-axis zoom — must be added BEFORE overlay listeners
  const brush = brushX()
    .extent([
      [0, 0],
      [innerWidth, innerHeight],
    ])
    .on("end", event => {
      if (isSyncingRef.current) return;
      if (!event.selection) return;

      const [x0, x1] = event.selection.map(v => xScale.invert(v));

      // Clear brush visually
      g.select(".brush").call(brush.move, null);

      if (onZoomChange) {
        onZoomChange({
          min: x0.getTime(),
          max: x1.getTime(),
          source: "line",
        });
      }
    });

  // Custom filter: only allow brush when shift is NOT pressed
  brush.filter(event => !event.shiftKey && !event.button);

  const brushGroup = g.append("g").attr("class", "brush");
  brushGroup.call(brush);

  // Style brush selection
  brushGroup
    .select(".selection")
    .attr("fill", colors.aiOptimization)
    .attr("fill-opacity", 0.15)
    .attr("stroke", colors.aiOptimization)
    .attr("stroke-width", 1);

  // Use the brush overlay rect for mouse events (tooltip, crosshair, pan)
  const brushOverlay = brushGroup.select(".overlay").style("cursor", "crosshair");

  // Collect all unique timestamps from visible series for snap-to-point behavior
  const allTimestamps = [];
  SERIES_CONFIG.forEach(cfg => {
    if (!visibility[cfg.name]) return;
    const sd = allSeriesData[cfg.key];
    if (sd)
      sd.forEach(d => {
        if (d[0] !== null && d[0] !== undefined) allTimestamps.push(d[0]);
      });
  });
  const uniqueTimestamps = [...new Set(allTimestamps)].sort((a, b) => a - b);

  // Mouse event handlers — attached to brush overlay so both brush & tooltip work
  brushOverlay
    .on("mousemove.tooltip", event => {
      const [mx] = pointer(event);
      const rawX = xScale.invert(mx).getTime();

      // Snap to nearest data timestamp (15-min granularity)
      let x = rawX;
      if (uniqueTimestamps.length > 0) {
        let closestTs = uniqueTimestamps[0];
        let minDist = Math.abs(rawX - closestTs);
        for (let i = 1; i < uniqueTimestamps.length; i++) {
          const dist = Math.abs(rawX - uniqueTimestamps[i]);
          if (dist < minDist) {
            minDist = dist;
            closestTs = uniqueTimestamps[i];
          } else {
            break;
          }
        }
        x = closestTs;
      }

      const snappedMx = xScale(x);
      crosshair.attr("x1", snappedMx).attr("x2", snappedMx).attr("opacity", 1);

      // Update hover markers
      markersGroup.selectAll("*").remove();
      SERIES_CONFIG.forEach(cfg => {
        if (!visibility[cfg.name]) return;
        const sd = allSeriesData[cfg.key];
        if (!sd || !sd.length) return;

        let point;
        if (cfg.isSetpoint) {
          const idx = findLeftIndex(sd, x);
          point = idx >= 0 ? sd[idx] : null;
        } else {
          point = findClosestPoint(sd, x);
        }

        if (point && point[0] !== null && point[0] !== undefined && point[1] !== null && point[1] !== undefined) {
          markersGroup
            .append("circle")
            .attr("cx", xScale(point[0]))
            .attr("cy", yScale(point[1]))
            .attr("r", 4)
            .attr("fill", cfg.color)
            .attr("stroke", isDark ? "#1e293b" : "#fff")
            .attr("stroke-width", 1.5);
        }
      });

      // Position tooltip
      const tooltipHTML = buildTooltipHTML(x, allSeriesData, visibility, siteTimezone);
      tooltip.html(tooltipHTML).style("opacity", 1).style("pointer-events", "none");

      const tooltipWidth = tooltipEl?.offsetWidth || CHART_CONSTANTS.TOOLTIP_DEFAULT_WIDTH;
      const tooltipHeight = tooltipEl?.offsetHeight || CHART_CONSTANTS.TOOLTIP_DEFAULT_HEIGHT;
      const containerRect = container.getBoundingClientRect();
      let left = event.clientX - containerRect.left + CHART_CONSTANTS.TOOLTIP_OFFSET_X;
      let top = event.clientY - containerRect.top - tooltipHeight / 2;

      // Flip tooltip if going off right edge
      if (left + tooltipWidth > containerRect.width - CHART_CONSTANTS.TOOLTIP_EDGE_PADDING) {
        left = event.clientX - containerRect.left - tooltipWidth - CHART_CONSTANTS.TOOLTIP_OFFSET_X;
      }
      if (top < 0) top = CHART_CONSTANTS.TOOLTIP_TOP_PADDING;
      if (top + tooltipHeight > containerRect.height) {
        top = containerRect.height - tooltipHeight - TOOLTIP_BOTTOM_PADDING;
      }

      tooltip.style("left", `${left}px`).style("top", `${top}px`);
    })
    .on("mouseleave.tooltip", () => {
      crosshair.attr("opacity", 0);
      markersGroup.selectAll("*").remove();
      tooltip.style("opacity", 0);
    });

  // Pan with shift+drag
  let panStart = null;
  let panDomainStart = null;

  brushOverlay.on("mousedown.pan", event => {
    if (event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      panStart = pointer(event)[0];
      panDomainStart = xScale.domain().map(d => d.getTime());
      brushOverlay.style("cursor", "grabbing");

      const onMouseMove = e => {
        if (panStart === null || panStart === undefined) return;
        const dx = pointer(e, svgEl)[0] - MARGIN.left - panStart;
        const domainWidth = panDomainStart[1] - panDomainStart[0];
        const pixelWidth = innerWidth;
        const shift = -(dx / pixelWidth) * domainWidth;

        const newMin = panDomainStart[0] + shift;
        const newMax = panDomainStart[1] + shift;

        if (onZoomChange && !isSyncingRef.current) {
          onZoomChange({ min: newMin, max: newMax, source: "line" });
        }
      };

      const onMouseUp = () => {
        panStart = null;
        panDomainStart = null;
        brushOverlay.style("cursor", "crosshair");
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);

      // Store cleanup so the React component can remove listeners on unmount
      if (panCleanupRef) {
        panCleanupRef.current = () => {
          document.removeEventListener("mousemove", onMouseMove);
          document.removeEventListener("mouseup", onMouseUp);
        };
      }
    }
  });

  // Double click to reset zoom
  brushOverlay.on("dblclick.reset", () => {
    if (onZoomChange) {
      onZoomChange(null);
    }
  });
}
